import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Alert, Form, Button, Card, Flex, Space, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { CustomForm } from "ui/Form"
import { useWatch } from "antd/es/form/Form"
import { useSelector } from "react-redux"
import ClientDetail from "./ClientDetail"
import ExperienceDetail from "./ExperienceDetail"
import PaymentMethod from "./PaymentMethod"
import PriceBreakdown, { CardFeeRows } from "./PriceBreakdown"
import PromoCodeInput from "ui/PromoCodeInput"
import Stepper from "./Stepper"
import styles from "./BookingForm.module.css"
import { Title } from "ui/Typography"
import { calculateAddOnsTotal } from "components/bookings/helpers"
import { priceWithBeforeAdjustments } from "utils/calculateBookingTotal"
import { resolveEffectiveDiscountPromos } from "utils/discountUtils"
import { getCardFeeFraction } from "utils/bookingCalculations"
import { getOrderCardFee, sumItemPrices } from "../helpers/priceLines"
import { priceTicketedSelection } from "../helpers/ticketedPrice"
import { isDateSelectionIncomplete } from "utils/multiDayPricing"
import { EXPERIENCE_TYPES } from "constants/experiences"
import { Icon } from "ui/Icon"
import AddedExperiencesCard from "./AddedExperiencesCard"
import ClientStrip from "./ClientStrip"
import OrderSummary from "./OrderSummary"
import OrderTotal from "./OrderTotal"
import useAddedExperiences from "./useAddedExperiences"
import { useDevice } from "hooks/useDevice"

const STEP1_BASE_FIELDS = ["experience", "date", "duration", "time", ["main", "adults"], "guestTotal"]
const STEP1_NEW_CLIENT_FIELDS = ["firstName", "lastName", "email", "phone_number"]
const STEP1_EXISTING_CLIENT_FIELDS = ["existing_client", "email"]
const EMPTY_PRICE = {
  amount: 0,
  baseAmount: 0,
  originalBaseAmount: 0,
  breakdown: [],
  preCardTotal: 0,
  preCardTotalBeforeAdjustments: 0,
  cardProcessingFee: 0
}

const BookingForm = ({
  form,
  bookingUuid,
  bookAgain,
  booking,
  onSubmit,
  onOrderSubmit,
  isOrderPending,
  filterExperienceIds,
  appliedPromos,
  onPromoApplied,
  onPromoRemoved,
  onClearPromos,
  onValuesChange,
  bookingSelections,
  updateBookingSelections,
  onExperienceViewChange
}) => {
  const { t } = useTranslation()
  const { isTablet } = useDevice()
  const [existingClientId, setExistingClientId] = useState(null)
  const [currentStep, setCurrentStep] = useState(1)
  const scrollContainerRef = useRef(null)
  const [summaryData, setSummaryData] = useState({})
  const step2FieldsRef = useRef(null)
  const mode = "guideBooking"
  const isExistingClient = useWatch("isExistingClient", form)
  const paymentType = useWatch("paymentType", form)
  const adjustedPrice = useWatch("adjustedPrice", form)
  const depositOnly = useWatch("depositOnly", form)
  const depositPercent = useWatch("depositPercent", form)
  const customDepositAmount = useWatch("customDepositAmount", form)
  const depositFraction = getCardFeeFraction({ depositOnly, depositPercent, customDepositAmount })
  const [alertMessage, setAlertMessage] = useState("")
  const [dateError, setDateError] = useState(false)
  const isSearching = useSelector((state) => state.bookings.existing_clients_list.loading)
  const assetAllocation = useSelector((state) => state.bookings.assetAllocation)
  const {
    experienceById: { experienceDetail }
  } = useSelector((state) => state.experiences)

  const isEdit = !!bookingUuid
  // This form is the create screen; `isEdit` is true only for book-again, which carries the source
  // booking's discount forward. Once the guide picks a different experience that discount no longer
  // belongs to the booking, so it must stop feeding the breakdown and the totals.
  // preserve: the experience Select unmounts on step 2, and a plain useWatch would then report no
  // experience at all — see the same note in BookingWindow.
  const selectedExperience = useWatch("experience", { form, preserve: true })
  const selectedExperienceUuid = selectedExperience?.value
  const isExperienceChanged =
    isEdit && !!booking?.trip?.uuid && !!selectedExperienceUuid && selectedExperienceUuid !== booking.trip.uuid
  // A ticketed experience. Compared by id, so a detail still loading for another pick doesn't count.
  // It can be chosen but not submitted until the guide-side ticketed booking exists
  // (guide-orders-backend.md, G5/G6).
  const isTicketedPicked =
    selectedExperience?.key != null &&
    String(experienceDetail?.id) === String(selectedExperience.key) &&
    experienceDetail?.experience_type === EXPERIENCE_TYPES.ticketed

  // Prices a selection the way step 2 shows it: the total, and the same booking without the date's
  // day/seasonal adjustments or the multi-day ramp, which the Order Summary strikes beside it when
  // lower (as step 1 does in ExperienceDetail). Null until the selection has guests and a duration.
  // `experience` is the one on the form, or an order's added experience.
  const priceExperienceSelections = useCallback(
    (experience, selections) => {
      if (experience?.experience_type === EXPERIENCE_TYPES.ticketed) {
        // Nothing chosen prices at nothing, so the summary clears with the last ticket.
        return (
          priceTicketedSelection({
            experience,
            selections,
            discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged),
            depositFraction
          }) || EMPTY_PRICE
        )
      }
      const counts = selections?.counts
      const hasGuestCounts =
        counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
      if (!hasGuestCounts || !selections?.tripLength) return null
      const guideFeePercent = experience?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      const priceOptions = {
        paymentType,
        isEdit,
        originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
        customFees: experience?.assigned_custom_fees,
        platformFeeSetting: experience?.platform_fee_setting || booking?.platform_fee_setting,
        addOnsCatalog: experience?.add_ons,
        discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged),
        dayAdjustments: experience?.day_adjustments,
        seasonalAdjustments: experience?.seasonal_adjustments,
        seasonRanges: experience?.available_date_ranges,
        trip: experience,
        depositFraction,
        overrideGuestSubtotal: adjustedPrice != null && adjustedPrice > 0 ? adjustedPrice : undefined
      }
      const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee, preCardTotalBeforeAdjustments } =
        priceWithBeforeAdjustments(selections, counts, guideFeePercent, priceOptions)
      return {
        amount: total,
        baseAmount: guestSubtotal,
        originalBaseAmount: calculatedGuestSubtotal,
        breakdown,
        preCardTotal,
        preCardTotalBeforeAdjustments,
        cardProcessingFee
      }
    },
    [booking, paymentType, isEdit, isExperienceChanged, appliedPromos, depositFraction, adjustedPrice]
  )
  const priceSelections = useCallback((selections) => priceExperienceSelections(experienceDetail, selections), [priceExperienceSelections, experienceDetail])
  // Whether a new price differs from the one on the selection (the effects below only recalculate
  // on their own triggers, and must not loop).
  const isPriceChanged = (price) =>
    !!price &&
    (price.amount !== bookingSelections?.amount || price.preCardTotalBeforeAdjustments !== bookingSelections?.preCardTotalBeforeAdjustments)

  // Stores a change to the selection with the price it comes to.
  const handleSelectionsChange = useCallback(
    (update) => updateBookingSelections({ ...update, ...priceSelections({ ...bookingSelections, ...update }) }),
    [updateBookingSelections, bookingSelections, priceSelections]
  )

  const handleAddOnsChange = useCallback(
    ({ target: { value: selections } }) =>
      handleSelectionsChange({ addOns: selections, addOnsTotal: calculateAddOnsTotal(selections, experienceDetail?.add_ons) }),
    [handleSelectionsChange, experienceDetail]
  )

  useEffect(() => {
    const price = priceSelections(bookingSelections)
    if (isPriceChanged(price)) updateBookingSelections(price)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedPromos])

  // The card processing fee is charged only on the deposit (pay-now) portion, so changing the
  // deposit % or payment type on step 2 must recompute the total. ExperienceDetail (which recalcs
  // on step 1) is unmounted on step 2, so handle the deposit/payment-type change here.
  useEffect(() => {
    const price = priceSelections(bookingSelections)
    if (isPriceChanged(price)) updateBookingSelections(price)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositFraction, paymentType])

  // Subtotal adjustment: recompute when the negotiated price changes. The override is read by
  // calculateBookingTotal via overrideGuestSubtotal; originalBaseAmount keeps the computed value
  // so the Order Summary can show the adjustment.
  useEffect(() => {
    const price = priceSelections(bookingSelections)
    if (price) updateBookingSelections(price)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustedPrice])

  // Step 1's checks. `isClientIncluded` false checks only the experience on the form, for "Add
  // Another Experience" and its Confirm.
  // `isExperienceIncluded` false checks only the client, for an order whose experiences are all in the
  // Added Experiences list (each was checked when it was added).
  const validateStep1 = async ({ isClientIncluded, isExperienceIncluded = true }) => {
    const clientFields = isExistingClient ? STEP1_EXISTING_CLIENT_FIELDS : STEP1_NEW_CLIENT_FIELDS
    const accessibilityRequired = form.getFieldValue("accessibility_required")
    const fieldsToValidate = [
      ...(isClientIncluded ? clientFields : []),
      ...(isExperienceIncluded ? STEP1_BASE_FIELDS : []),
      ...(isExperienceIncluded && accessibilityRequired ? ["accessibility_notes"] : [])
    ]
    // The registered `date` field only tracks the DateSelector's value for form.getFieldsValue
    // (see its `hidden` Form.Item in ExperienceDetail.jsx) — "has a valid date/range been picked"
    // is checked explicitly here instead, so the error is always visible on the date field. Checked
    // before the other fields, whose own errors (e.g. "Please select a time slot") would otherwise
    // stop here first and leave a missing date unflagged.
    const isDateMissing = isExperienceIncluded && isDateSelectionIncomplete(experienceDetail, bookingSelections)
    setDateError(isDateMissing)
    try {
      await form.validateFields(fieldsToValidate)
    } catch {
      return false
    }
    if (isDateMissing) return false
    if (isClientIncluded && alertMessage) return false
    // Don't allow proceeding when asset allocation failed (e.g. insufficient capacity).
    if (isExperienceIncluded && assetAllocation?.error) return false
    return true
  }

  const addedExperiences = useAddedExperiences({
    form,
    bookingSelections,
    updateBookingSelections,
    experienceDetail,
    validateExperience: () => validateStep1({ isClientIncluded: false }),
    // The date field's error belongs to the experience that was on the form.
    onFormReplaced: () => setDateError(false)
  })
  // The Add / Edit Experience view takes over the window (Figma 5795:175252, 5851:189881): the window's
  // header becomes "< Back", which cancels it like Cancel does. The window gets one stable callback for
  // as long as the view is open; the hook's own cancel changes every render.
  const cancelExperienceViewRef = useRef(addedExperiences.cancel)
  cancelExperienceViewRef.current = addedExperiences.cancel
  const isExperienceViewOpen = !!addedExperiences.sheet
  useEffect(() => {
    onExperienceViewChange?.(isExperienceViewOpen ? () => cancelExperienceViewRef.current() : null)
  }, [isExperienceViewOpen, onExperienceViewChange])

  // An order of several experiences (the list), booked as one guide order (guide-orders-backend.md,
  // G1/G2). The single booking's subtotal adjustment, deposit and promo code wait on the order's rules
  // (G3, questions 3 and 5), so an order has none of them yet.
  const isOrder = addedExperiences.items.length > 1
  // A ticketed booking has no subtotal adjustment, deposit or promo code until its rules exist.
  const isTicketedBooking = isOrder ? addedExperiences.items.some((item) => !!item.selections?.scheduledSlot) : isTicketedPicked
  const isPriceOptionsShown = !isOrder && !isTicketedBooking
  // An order books its ticketed experiences (POST /guide/orders takes ticket lines); a single booking
  // can't yet, as its create sends no start time or tickets.
  const isTicketedSubmitBlocked = !isOrder && isTicketedPicked
  // An order's experiences priced for the payment method chosen on step 2 (cash carries no platform
  // fee): each was priced when it was added, before there was one.
  const orderItems = useMemo(
    () =>
      addedExperiences.items.map((item) => ({
        ...item,
        selections: { ...item.selections, ...priceExperienceSelections(item.experience, item.selections) }
      })),
    [addedExperiences.items, priceExperienceSelections]
  )
  const orderTotals = sumItemPrices(orderItems)
  const orderCardFee = isOrder && paymentType === "card" ? getOrderCardFee(orderItems, orderTotals.total) : 0
  // The experience the API refused on Submit ({ key, message }): marked on step 2 and in the list
  // until the guide changes the list (Confirm in its Edit view, or Remove).
  const [refusedItem, setRefusedItem] = useState(null)
  useEffect(() => {
    setRefusedItem(null)
  }, [addedExperiences.items])
  // "Change" on the refused experience: back to step 1, in its Edit view.
  const handleChangeRefused = (key) => {
    onClearPromos?.()
    setCurrentStep(1)
    addedExperiences.edit(key)
  }
  // A booking (or an order) discounted to nothing has no charge to place, so asking how to take it is
  // noise: the whole payment-method card is dropped and the booking saves as cash (see
  // submitTripBooking, which also keeps the card and the Stripe intent out of it).
  const hasNothingToCollect = Number(isOrder ? orderTotals.total : (bookingSelections?.amount ?? 0)) <= 0

  const handleContinue = async () => {
    if (!(await validateStep1({ isClientIncluded: true, isExperienceIncluded: !addedExperiences.isListShown }))) return
    setSummaryData(form.getFieldsValue(true))
    setCurrentStep(2)
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  useEffect(() => {
    if (currentStep === 2 && step2FieldsRef.current) {
      form.setFieldsValue(step2FieldsRef.current)
    }
  }, [currentStep, form])

  const clientDisplayName = summaryData.isExistingClient
    ? summaryData.existing_client?.label
    : [summaryData.firstName, summaryData.lastName].filter(Boolean).join(" ")
  const summaryEmail = summaryData.email
  // The Price Details line for the booking's discount: the codes applied here, or (book again) the
  // source booking's own discount.
  const isDiscounted = resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged).length > 0
  const appliedCodes = appliedPromos.map((promo) => promo.code).filter(Boolean).join(", ")
  let discountLabel = null
  if (isDiscounted) {
    discountLabel = appliedCodes ? t("booking.order_summary.discount_with_code", { code: appliedCodes }) : t("booking.order_summary.discount")
  }

  // The experience on the form can't move on: a ticketed one (ExperienceDetail says why), or one the
  // asset allocation refused. The Added Experiences list has no experience on the form.
  const isFormExperienceBlocked = !!assetAllocation?.error
  let footerActions
  if (currentStep === 1 && addedExperiences.sheet) {
    footerActions = (
      <>
        <Button variant="outlined" color="primary" htmlType="button" onClick={addedExperiences.cancel}>
          {t("global.cancel")}
        </Button>
        <Button type="primary" htmlType="button" onClick={addedExperiences.confirm} disabled={isFormExperienceBlocked}>
          {t("booking.added_experiences.confirm")}
        </Button>
      </>
    )
  } else if (currentStep === 1) {
    // No Cancel, as in the redesign: the window's X closes it.
    const isStep1Blocked = !addedExperiences.isListShown && isFormExperienceBlocked
    footerActions = (
      <>
        <Button
          variant="outlined"
          color="primary"
          htmlType="button"
          icon={<Icon type="feather" name="Plus" width={20} height={20} color="currentColor" />}
          onClick={addedExperiences.addAnother}
          disabled={isStep1Blocked}
        >
          {t("booking.added_experiences.add_another")}
        </Button>
        <Button type="primary" htmlType="button" onClick={handleContinue} disabled={isStep1Blocked}>
          {t("booking.continue_to_payment")}
        </Button>
      </>
    )
  } else {
    footerActions = (
      <>
        {/* An unpaid order's bookings already exist, so its experiences can't change any more. */}
        {!isOrderPending && (
          <Button
            variant="outlined"
            color="primary"
            htmlType="button"
            onClick={() => {
              onClearPromos?.()
              setCurrentStep(1)
            }}
          >
            {t("global.previous")}
          </Button>
        )}
        <Button
          type="primary"
          htmlType="button"
          disabled={isTicketedSubmitBlocked}
          onClick={async () => {
            try {
              // Nothing to collect means no method to choose, so the hidden (still
              // required) field must not block the save.
              if (!hasNothingToCollect) await form.validateFields(["paymentType"])
              if (isOrder) {
                const outcome = await onOrderSubmit({ items: addedExperiences.items, total: orderTotals.total })
                setRefusedItem(outcome?.refusedItem ?? null)
              } else onSubmit()
            } catch {}
          }}
        >
          {bookingUuid && !bookAgain ? t("global.update") : t("global.submit")}
        </Button>
      </>
    )
  }

  return (
    <Spin spinning={isSearching}>
      <CustomForm layout="vertical" form={form} onValuesChange={onValuesChange}>
        {!isExperienceViewOpen && <Stepper activeStep={currentStep} t={t} />}
        <div className={`${styles.scrollContainer} ${isTablet ? styles.scrollContainerFill : ""}`} ref={scrollContainerRef}>
          {currentStep === 1 ? (
            <>
              {/* The Add / Edit Experience view (several experiences) shows only that experience. */}
              <Card className={styles.cardContainer} style={{ display: addedExperiences.sheet ? "none" : undefined }}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.client_detail")}
                </Title>
                <ClientDetail
                  setExistingClientId={setExistingClientId}
                  form={form}
                  bookingId={bookingUuid}
                  alertMessage={alertMessage}
                  setAlertMessage={setAlertMessage}
                  booking={booking}
                />
              </Card>
              {isExperienceViewOpen && (
                <Title className={styles.experienceViewTitle} level={5}>
                  {addedExperiences.sheet.editingKey ? t("booking.added_experiences.edit_title") : t("booking.add_experience")}
                </Title>
              )}
              {addedExperiences.isListShown ? (
                <AddedExperiencesCard
                  items={orderItems}
                  refusedItem={refusedItem}
                  onEdit={addedExperiences.edit}
                  onRemove={addedExperiences.remove}
                />
              ) : (
                <Card className={styles.cardContainer}>
                  {!isExperienceViewOpen && (
                    <Title className={styles.cardTitle} level={5}>
                      {t("booking.add_experience")}
                    </Title>
                  )}
                  <ExperienceDetail
                    key={addedExperiences.formKey}
                    form={form}
                    bookingId={bookingUuid}
                    booking={booking}
                    bookAgain={bookAgain}
                    mode={mode}
                    filterExperienceIds={filterExperienceIds}
                    appliedPromos={appliedPromos}
                    bookingSelections={bookingSelections}
                    updateBookingSelections={updateBookingSelections}
                    dateError={dateError}
                    setDateError={setDateError}
                    onAddOnsChange={handleAddOnsChange}
                    onSelectionsChange={handleSelectionsChange}
                  />
                </Card>
              )}
            </>
          ) : (
            <>
              <ClientStrip name={clientDisplayName} email={summaryEmail} phone={summaryData.phone_number?.fullNumber} />
              <OrderSummary
                items={
                  isOrder
                    ? orderItems.map(({ key, experience, selections }) => ({
                        key,
                        experience,
                        selections,
                        error: refusedItem?.key === key ? refusedItem.message : null
                      }))
                    : [{ key: "current", experience: experienceDetail, selections: bookingSelections, discountLabel }]
                }
                onChangeItem={handleChangeRefused}
                footer={
                  <OrderTotal
                    total={isOrder ? orderTotals.total : Number(bookingSelections?.preCardTotal ?? bookingSelections?.amount) || 0}
                    totalBeforeAdjustments={
                      isOrder ? orderTotals.beforeAdjustments : Number(bookingSelections?.preCardTotalBeforeAdjustments) || 0
                    }
                    isPromoApplied={appliedPromos.length > 0}
                    promoInput={
                      isPriceOptionsShown && (
                        <PromoCodeInput
                          totalAmount={bookingSelections?.baseAmount ?? 0}
                          addOnsTotal={bookingSelections?.addOnsTotal ?? 0}
                          appliedPromos={appliedPromos}
                          onPromoApplied={onPromoApplied}
                          onPromoRemoved={onPromoRemoved}
                          experienceId={summaryData.experience?.key}
                            clientIdentifier={summaryData.email || summaryData.phone_number?.fullNumber}
                          />
                      )
                    }
                  >
                    <Form.Item name="adjustedPrice" hidden noStyle>
                      <input type="hidden" />
                    </Form.Item>
                    <Form.Item name="adjustmentReason" hidden noStyle>
                      <input type="hidden" />
                    </Form.Item>
                    {/* Until the client decides how these fit the redesign (questions 66–67): the
                        subtotal adjustment, and what is charged now (card fee, deposit). */}
                    {isPriceOptionsShown && (
                      <PriceBreakdown
                        form={form}
                        booking={booking}
                        appliedPromos={appliedPromos}
                        isEdit={isEdit}
                        isExperienceChanged={isExperienceChanged}
                        isChargesOnly
                        bookingSelections={bookingSelections}
                      />
                    )}
                    {orderCardFee > 0 && <CardFeeRows cardProcessingFee={orderCardFee} chargedAmount={orderTotals.total + orderCardFee} />}
                  </OrderTotal>
                }
              />
              <Card className={styles.cardContainer} style={{ display: hasNothingToCollect ? "none" : undefined }}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.payment_method")}
                </Title>
                <PaymentMethod
                  existingClientId={existingClientId}
                  setExistingClientId={setExistingClientId}
                  form={form}
                  bookingId={bookingUuid}
                  booking={booking}
                  bookAgain={bookAgain}
                  isPaymentRequired={!hasNothingToCollect}
                  isDepositOffered={isPriceOptionsShown}
                  appliedPromos={appliedPromos}
                  bookingSelections={bookingSelections}
                  updateBookingSelections={updateBookingSelections}
                />
              </Card>
              {isTicketedSubmitBlocked && <Alert type="info" showIcon message={t("booking.ticketed.submit_unavailable")} />}
              {isOrderPending && <Alert type="warning" showIcon message={t("booking.added_experiences.order_pending")} />}
            </>
          )}
        </div>
        <Form.Item className={styles.formActions}>
          {/* A tablet's buttons share the width (Figma 5846:125628). wrap: on a phone, Add Another
              Experience and Continue don't fit on one line. */}
          {isTablet ? (
            <Flex gap="var(--spacing-s)" className={styles.tabletActions}>
              {footerActions}
            </Flex>
          ) : (
            <Space wrap>{footerActions}</Space>
          )}
        </Form.Item>
      </CustomForm>
    </Spin>
  )
}

BookingForm.propTypes = {
  form: PropTypes.object.isRequired,
  bookingUuid: PropTypes.string.isRequired,
  bookAgain: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  // Submit for several experiences: called with the order, { items, total }; resolves with
  // { refusedItem } when the API refused one of them.
  onOrderSubmit: PropTypes.func.isRequired,
  // An order is booked but its card payment failed: only paying it is left.
  isOrderPending: PropTypes.bool,
  filterExperienceIds: PropTypes.arrayOf(PropTypes.number),
  appliedPromos: PropTypes.array.isRequired,
  onPromoApplied: PropTypes.func.isRequired,
  onPromoRemoved: PropTypes.func.isRequired,
  onClearPromos: PropTypes.func,
  onValuesChange: PropTypes.func,
  bookingSelections: PropTypes.object.isRequired,
  updateBookingSelections: PropTypes.func.isRequired,
  // Told the Add / Edit Experience view's back action while it is open, null once it closes.
  onExperienceViewChange: PropTypes.func
}

export default BookingForm
